/**
 * @Author: wuyang
 * @Date: 2023/12/28
 * @Description: ""
 */
import { atom, Atom, PrimitiveAtom, WritableAtom } from 'jotai'
import { createStore, IAtomMethod } from '@/helper/store/createStore'
import mitt, { Emitter, EventType, Handler } from 'mitt'

export interface IBaseEventMitt {
	[key: EventType]: any
}
export interface IBaseAtomMethodClassProps<
	EventMitt extends IBaseEventMitt = IBaseEventMitt
> {
	handleMitt?: { [key in keyof EventMitt]?: Handler<EventMitt[key]> }
}
export class BaseAtomMethodClass<
	Props extends IBaseAtomMethodClassProps,
	EventMitt extends Record<EventType, unknown> = any
> {
	private __unsub_callback__: Array<() => void> = []
	private __store__ = createStore()

	// select 的缓存
	private __atomMethodSelectMap__ = new Map<any, IAtomMethod>()
	// atom 方法的缓存
	private __atomMethodMap__ = new Map<Atom<any>, IAtomMethod<any>>()

	private __mitt = mitt()

	constructor(props: Props) {
		const handleMitt = props?.handleMitt
		if (handleMitt) {
			for (const handleMittKey in handleMitt) {
				this.mitt.on(handleMittKey as any, handleMitt[handleMittKey] as any)
			}
		}
	}

	// 销毁前要执行的函数
	public addBeforeDestoryCallback(callback: any) {
		if (typeof callback === 'function') {
			this.__unsub_callback__.push(callback)
		}
	}

	// 可被子类复写 函数销毁方法， 记得 super.destory()
	public destory() {
		this.__unsub_callback__.forEach((unsub) => unsub())
		this.__unsub_callback__ = []
		this.mitt.all.clear()
	}

	get mitt() {
		return this.__mitt as unknown as Emitter<EventMitt>
	}

	/**
	 * 当前对象 atom store 空间
	 */
	get store() {
		return this.__store__
	}

	// jotai 默认带的 selectAtom 方法无法再原数据变化的情况下触发 select 继续监听变化，所以要自己实现
	/* 灵活获取切割之后的数据 */
	selectAtom<T extends any, P extends any>(
		methodAtom: IAtomMethod<P>,
		selector: (atom: P) => T,
		options?: {
			equalityFn?: (a: T, b: T) => boolean
			key?: string
		}
	): IAtomMethod<T> {
		const { equalityFn = Object.is, key = Symbol() } = options ?? {}
		if (this.__atomMethodSelectMap__.has(key)) {
			return this.__atomMethodSelectMap__.get(key) as IAtomMethod<T>
		}
		const resultAtomMethod = this.genAtomMethod(
			atom(selector(methodAtom.get()))
		)

		const unsub = methodAtom.sub(
			({ oldValue: oldMethodAtom, newValue: newMethodAtom }) => {
				const newValue = selector(newMethodAtom)
				const oldValue = selector(oldMethodAtom)
				if (!equalityFn(newValue, oldValue)) {
					resultAtomMethod.set(newValue)
				}
			},
			{}
		)
		this.__unsub_callback__.push(unsub)
		this.__atomMethodSelectMap__.set(key, resultAtomMethod as IAtomMethod<any>)
		return resultAtomMethod
	}

	/**
	 * 根据 store 生成单值对应的方法
	 */
	genAtomMethod<T extends any>(atom: Atom<T>): IAtomMethod<T> {
		if (!this.__atomMethodMap__.has(atom)) {
			const method: IAtomMethod<T> = {
				get: () => {
					return this.__store__.get(atom)
				},
				set: (value, options) => {
					return this.__store__.set(
						atom as WritableAtom<any, any, any>,
						value,
						options
					) as void
				},
				del: () => {
					const data = this.__store__.get(atom)
					try {
						this.__store__.del(atom as WritableAtom<any, any, any>)
					} catch (e) {}
					return data
				},
				sub: (listener, options) => {
					// 这里每次监听都要将返回值记录一下，对象卸载的时候也要取消绑定
					const unsub = this.__store__.sub(atom, listener, options)
					this.__unsub_callback__.push(unsub)
					return () => {
						this.__unsub_callback__.filter((sub) => sub === unsub)
						unsub()
					}
				},
				error: (value, options) => {
					return this.__store__.set(
						atom as WritableAtom<any, any, any>,
						{
							...value,
							status: 'error',
							isError: true
						},
						options
					) as void
				},
				atom: () => {
					return atom as PrimitiveAtom<T>
				}
			}
			this.__atomMethodMap__.set(atom, method)
		}
		return this.__atomMethodMap__.get(atom) as IAtomMethod<T>
	}
}
